import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import Files from '../../Upload/Files';
import MainButton from '../MainButton';
import { efesBorder, efesError, efesGreen, white } from '../../../theme';

import { ReactComponent as FileIcon } from '../../../styles/svgs/uploaded-file.svg';
import { ReactComponent as ImageIcon } from '../../../styles/svgs/image-regular.svg';
import useStyles from './makeStyles';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderWidth: 1,
  borderRadius: 4,
  minHeight: 75,
  borderColor: efesBorder,
  backgroundColor: white,
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
  marginTop: '.5rem',
  marginBottom: '.5rem',
  cursor: 'pointer',
};

const filledStyles = {
  borderColor: 'transparent',
};

const acceptStyle = {
  borderColor: efesGreen,
};

const rejectStyle = {
  borderColor: efesError,
};

const ICONS = {
  file: FileIcon,
  image: ImageIcon,
};

const MUIDropzone = ({
  handleAddFiles,
  files,
  fileData,
  icon,
  handleRemoveFile,
  title,
  description,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const Icon = ICONS[icon];
  const { fileStates, errors } = fileData;
  const fileInputRef = useRef(null);
  const [isDragEntered, setIsDragEntered] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles) => {
      handleAddFiles(acceptedFiles);
      setIsDragEntered(false);
    },
    [handleAddFiles],
  );

  const { getRootProps, isDragAccept, isDragReject, isDragActive, getInputProps } = useDropzone({
    onDrop,
  });

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
      ...(files.length ? filledStyles : {}),
      ...(isDragActive || isDragEntered ? acceptStyle : {}),
    }),
    [isDragAccept, isDragReject, files.length, isDragActive, isDragEntered],
  );

  return (
    <Grid container direction="column" className={classes.dropZoneContainer}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Grid container direction="column">
            {title && (
              <Grid item>
                <Typography className={classes.title}>
                  {title}
                </Typography>
              </Grid>
            )}
            {description && (
              <Grid item>
                <Typography color="secondary">
                  {description}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item>
          <MainButton
            className={classes.attachmentButton}
            startIcon={<AttachFileIcon style={{ transform: 'rotate(45deg)' }} />}
            onClick={handleButtonClick}
            variant="text"
            color="primary"
            width="fullWidth"
          >
            {t('upload.addAttachment')}
          </MainButton>
        </Grid>
      </Grid>

      <div
        onDragLeaveCapture={() => setIsDragEntered(false)}
        onDragOver={() => setIsDragEntered(true)}
      >
        <input {...getInputProps()} />
        <Grid
          item
          {...getRootProps({
            style,
          })}
          ref={fileInputRef}
        >
          {!files.length && (
            <Grid item>
              <Typography
                className={classes.label}
                dangerouslySetInnerHTML={{
                  __html: t('upload.dragAndDrop'),
                }}
              />
            </Grid>
          )}
          <Files
            files={files}
            fileStates={fileStates}
            errors={errors}
            Icon={Icon}
            handleRemoveFile={handleRemoveFile}
          />
        </Grid>
      </div>
    </Grid>
  );
};

MUIDropzone.propTypes = {
  handleAddFiles: PropTypes.func.isRequired,
  handleRemoveFile: PropTypes.func.isRequired,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      isImage: PropTypes.bool.isRequired,
    }),
  ).isRequired,
  fileData: PropTypes.shape({
    fileStates: PropTypes.shape({}).isRequired,
    errors: PropTypes.shape({}).isRequired,
  }).isRequired,
  icon: PropTypes.oneOf(['file', 'image']).isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  description: PropTypes.string,
};

MUIDropzone.defaultProps = {
  title: null,
  description: null,
};

export default MUIDropzone;
